import React, { useCallback, useMemo } from 'react';
import { getLogger } from 'src/utils/logger';
import { submitPersonalisedLearningSupportPlan } from 'src/services/document.service';
import { generateSmartGoals as generateSmartGoalsService } from 'src/services/smart-goals.service';
import { generateStrategies as generateStrategiesService } from 'src/services/strategies.service';
import { useDocumentForm } from 'src/hooks/useDocumentForm';
import { useSmartGoalsGeneration } from 'src/hooks/useSmartGoalsGeneration';
import { useStrategiesGeneration } from 'src/hooks/useStrategiesGeneration';
import { useDocumentDownload } from 'src/hooks/useDocumentDownload';
import { useUsage } from 'src/hooks/useUsage';
import { PersonalisedLearningSupportPlanData } from 'src/models/form-types';
import { DocumentDownload } from 'src/components/document-download/DocumentDownload';
import { DocumentResponse } from 'src/models/api-response';
import { UsageIndicator } from 'src/components/common/UsageIndicator';
import { MissingFieldsSummary, MissingField } from 'src/components/common/MissingFieldsSummary';

// Import form sections
import { StudentInfoSection } from './sections/StudentInfoSection';
import { PurposeSection } from './sections/PurposeSection';
import { LearningSection } from './sections/LearningSection';
import { AdditionalInfoSection } from './sections/AdditionalInfoSection';
import { LevelOfAdjustmentSection } from './sections/LevelOfAdjustmentSection';
import { LiteracyGoalSection } from './sections/LiteracyGoalSection';
import { LiteracyStrategiesSection } from './sections/LiteracyStrategiesSection';
import { NumeracyGoalSection } from './sections/NumeracyGoalSection';
import { NumeracyStrategiesSection } from './sections/NumeracyStrategiesSection';
import { OtherGoalSection } from './sections/OtherGoalSection';
import { OtherStrategiesSection } from './sections/OtherStrategiesSection';

const logger = getLogger('PersonalisedLearningSupportPlanForm');

interface PersonalisedLearningSupportPlanFormProps {
  onBack: () => void;
  onSubmit?: (data: PersonalisedLearningSupportPlanData) => void;
}

// Initial form data
const initialFormData: PersonalisedLearningSupportPlanData = {
  // Student Information
  studentAge: '',
  yearLevel: '',
  strengths: '',
  
  // Purpose
  purpose: [],
  otherPurpose: '',
  
  // Learning & Support Information
  learningSupport: [],
  adhdType: '',
  autismLevel: '',
  hasSpLD: false,
  hasRMP: false,
  hasBRP: false,
  hasBSP: false,
  otherLearningSupport: '',

  
  // Additional Notes
  additionalInfo: [],
  otherAdditionalInfo: '',
  
  // Level of Adjustment
  levelOfAdjustment: [],
  
  // Learning Goals
  literacyTarget: '',
  literacyGoalInfo: '',
  literacyGoal: '',
  literacyStrategies: [],
  
  numeracyGoalInfo: '',
  numeracyGoal: '',
  numeracyStrategies: [],
  
  // Optional third goal
  otherGoalInfo: '',
  otherGoalType: '',
  otherGoal: '',
  otherStrategies: []
};

export const PersonalisedLearningSupportPlanForm: React.FC<PersonalisedLearningSupportPlanFormProps> = ({
  onBack,
  onSubmit
}) => {
  // Usage tracking - get usage from shared context
  const { getUsageForProduct, loading: usageLoading, refresh: refreshUsage } = useUsage();
  const usage = getUsageForProduct('personalised_plan');
  const canUse = usage?.can_use ?? false;

  // Document Download hook
  const {
    isDownloadVisible,
    documentId,
    base64Content,
    showDownload,
    hideDownload
  } = useDocumentDownload();

  // Use the document form hook for form state management
  const {
    formState,
    handleTextChange,
    handleCheckboxChange,
    submitForm,
    isSubmitting
  } = useDocumentForm<PersonalisedLearningSupportPlanData, DocumentResponse>({
    initialData: initialFormData,
    submitService: submitPersonalisedLearningSupportPlan,
    onSubmitSuccess: response => {
      logger.debug(`Form submission successful:`, response);
      // Show document Download when submission is successful
      if (response && response.documentId) {
        logger.info("About to show Download for document:", response.documentId);
        showDownload(response);
        // Refresh usage after successful generation
        refreshUsage();
      } else {
        logger.warn("No document ID in response:", response);
      }
      if (onSubmit) {
        onSubmit(formState.formData);
      }
    },
  });

  // Literacy goal generation
  const generateLiteracyGoalsWithCount = useCallback(
    (formData: PersonalisedLearningSupportPlanData, accessToken: string) => 
      generateSmartGoalsService({ ...formData, goalType: 'literacy' }, 3, accessToken),
    []
  );

  const {
    goals: literacyGoals,
    isLoading: loadingLiteracyGoals,
    error: literacyGoalsError,
    selectedGoal: selectedLiteracyGoal,
    generateSmartGoals: generateLiteracyGoals,
    selectGoal: selectLiteracyGoal
  } = useSmartGoalsGeneration(
    generateLiteracyGoalsWithCount,
    formState.formData
  );

  // Literacy strategies generation
  const generateLiteracyStrategiesWithGoal = useCallback(
    (data: { smartGoal: string, numStrategies: number }, accessToken: string) => {
      const cleanData = {
        smartGoal: String(data.smartGoal),
        numStrategies: Number(data.numStrategies)
      };
      return generateStrategiesService(cleanData, accessToken);
    },
    []
  );

  const {
    strategies: literacyStrategies,
    isLoading: loadingLiteracyStrategies,
    error: literacyStrategiesError,
    selectedStrategies: selectedLiteracyStrategies,
    generateStrategies: generateLiteracyStrategies,
    toggleStrategySelection: toggleLiteracyStrategySelection
  } = useStrategiesGeneration(
    generateLiteracyStrategiesWithGoal,
    selectedLiteracyGoal
  );

  // Numeracy goal generation
  const generateNumeracyGoalsWithCount = useCallback(
    (formData: PersonalisedLearningSupportPlanData, accessToken: string) => 
      generateSmartGoalsService({ ...formData, goalType: 'numeracy' }, 3, accessToken),
    []
  );

  const {
    goals: numeracyGoals,
    isLoading: loadingNumeracyGoals,
    error: numeracyGoalsError,
    selectedGoal: selectedNumeracyGoal,
    generateSmartGoals: generateNumeracyGoals,
    selectGoal: selectNumeracyGoal
  } = useSmartGoalsGeneration(
    generateNumeracyGoalsWithCount,
    formState.formData
  );

  // Numeracy strategies generation
  const generateNumeracyStrategiesWithGoal = useCallback(
    (data: { smartGoal: string, numStrategies: number }, accessToken: string) => {
      const cleanData = {
        smartGoal: String(data.smartGoal),
        numStrategies: Number(data.numStrategies)
      };
      return generateStrategiesService(cleanData, accessToken);
    },
    []
  );

  const {
    strategies: numeracyStrategies,
    isLoading: loadingNumeracyStrategies,
    error: numeracyStrategiesError,
    selectedStrategies: selectedNumeracyStrategies,
    generateStrategies: generateNumeracyStrategies,
    toggleStrategySelection: toggleNumeracyStrategySelection
  } = useStrategiesGeneration(
    generateNumeracyStrategiesWithGoal,
    selectedNumeracyGoal
  );

  // Other goal generation
  const generateOtherGoalsWithCount = useCallback(
    (formData: PersonalisedLearningSupportPlanData, accessToken: string) => 
      generateSmartGoalsService({ ...formData, goalType: 'other' }, 3, accessToken),
    []
  );

  const {
    goals: otherGoals,
    isLoading: loadingOtherGoals,
    error: otherGoalsError,
    selectedGoal: selectedOtherGoal,
    generateSmartGoals: generateOtherGoals,
    selectGoal: selectOtherGoal
  } = useSmartGoalsGeneration(
    generateOtherGoalsWithCount,
    formState.formData
  );

  // Other strategies generation
  const generateOtherStrategiesWithGoal = useCallback(
    (data: { smartGoal: string, numStrategies: number }, accessToken: string) => {
      const cleanData = {
        smartGoal: String(data.smartGoal),
        numStrategies: Number(data.numStrategies)
      };
      return generateStrategiesService(cleanData, accessToken);
    },
    []
  );

  const {
    strategies: otherStrategies,
    isLoading: loadingOtherStrategies,
    error: otherStrategiesError,
    selectedStrategies: selectedOtherStrategies,
    generateStrategies: generateOtherStrategies,
    toggleStrategySelection: toggleOtherStrategySelection
  } = useStrategiesGeneration(
    generateOtherStrategiesWithGoal,
    selectedOtherGoal
  );

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if at least 1 strategy is selected for literacy and numeracy
    if (selectedLiteracyStrategies.length < 1 || selectedNumeracyStrategies.length < 1) {
      logger.warn("Form submission attempted with fewer than 1 strategy for literacy or numeracy");
      return; // Prevent form submission
    }
    
    // Add the selected goals and strategies to the form data before submission
    formState.formData.literacyGoal = selectedLiteracyGoal || '';
    formState.formData.literacyStrategies = selectedLiteracyStrategies;
    formState.formData.numeracyGoal = selectedNumeracyGoal || '';
    formState.formData.numeracyStrategies = selectedNumeracyStrategies;
    
    // Only include other goal and strategies if they are selected
    if (selectedOtherGoal) {
      formState.formData.otherGoal = selectedOtherGoal;
      formState.formData.otherStrategies = selectedOtherStrategies;
    }
    
    logger.info("Submitting form...");
    await submitForm();
  }, [
    submitForm, 
    formState.formData, 
    selectedLiteracyGoal, 
    selectedLiteracyStrategies,
    selectedNumeracyGoal,
    selectedNumeracyStrategies,
    selectedOtherGoal,
    selectedOtherStrategies
  ]);

  // Check if each section has at least one checkbox selected OR something in the "other" field
  const isSectionValid = useCallback((checkboxArray: string[], otherField: string) => {
    return checkboxArray.length > 0 || otherField.trim() !== '';
  }, []);

  // Special check for PLASP learning support section that includes boolean fields
  const isPlaspLearningSupportSectionValid = useCallback((
    learningSupport: string[], 
    otherLearningSupport: string,
    hasSpLD: boolean,
    hasRMP: boolean,
    hasBRP: boolean,
    hasBSP: boolean
  ) => {
    return learningSupport.length > 0 || 
           otherLearningSupport.trim() !== '' || 
           hasSpLD || 
           hasRMP || 
           hasBRP || 
           hasBSP;
  }, []);

  // Validate all required sections
  const formValidation = useMemo(() => {
    const { 
      purpose, otherPurpose,
      learningSupport, otherLearningSupport, hasSpLD, hasRMP, hasBRP, hasBSP,
      additionalInfo, otherAdditionalInfo,
      levelOfAdjustment
    } = formState.formData;

    return {
      isPurposeValid: isSectionValid(purpose, otherPurpose),
      isLearningValid: isPlaspLearningSupportSectionValid(
        learningSupport, 
        otherLearningSupport,
        hasSpLD,
        hasRMP,
        hasBRP,
        hasBSP
      ),
      isAdditionalInfoValid: isSectionValid(additionalInfo, otherAdditionalInfo),
      isAdjustmentLevelValid: levelOfAdjustment.length > 0,
      isStudentInfoValid: formState.formData.studentAge.trim() !== '' && 
                          formState.formData.yearLevel.trim() !== '' && 
                          formState.formData.strengths.trim() !== ''
    };
  }, [formState.formData, isSectionValid, isPlaspLearningSupportSectionValid]);

  // Check if all required sections are valid
  const isFormValid = useMemo(() => {
    return (
      formValidation.isPurposeValid &&
      formValidation.isLearningValid &&
      formValidation.isAdditionalInfoValid &&
      formValidation.isAdjustmentLevelValid &&
      formValidation.isStudentInfoValid
    );
  }, [formValidation]);

  // Check if enough strategies are selected
  const hasEnoughLiteracyStrategies = selectedLiteracyStrategies.length >= 1;
  const hasEnoughNumeracyStrategies = selectedNumeracyStrategies.length >= 1;
  const hasEnoughOtherStrategies = !selectedOtherGoal || selectedOtherStrategies.length >= 1;
  
  const hasEnoughStrategies = hasEnoughLiteracyStrategies &&
                              hasEnoughNumeracyStrategies &&
                              hasEnoughOtherStrategies;

  // Build a unified list of missing required sections so the user can see
  // exactly what still needs to be filled in before generating.
  const missingFields = useMemo<MissingField[]>(() => {
    const items: MissingField[] = [];
    if (!formValidation.isStudentInfoValid) items.push({ label: 'Student information', anchorId: 'plasp-section-student-info' });
    if (!formValidation.isPurposeValid) items.push({ label: 'Purpose', anchorId: 'plasp-section-purpose' });
    if (!formValidation.isLearningValid) items.push({ label: 'Learning & Support', anchorId: 'plasp-section-learning' });
    if (!formValidation.isAdditionalInfoValid) items.push({ label: 'Additional information', anchorId: 'plasp-section-additional-info' });
    if (!formValidation.isAdjustmentLevelValid) items.push({ label: 'Level of adjustment', anchorId: 'plasp-section-level-of-adjustment' });
    if (!hasEnoughLiteracyStrategies) items.push({ label: 'Select at least one literacy strategy', anchorId: 'plasp-section-literacy' });
    if (!hasEnoughNumeracyStrategies) items.push({ label: 'Select at least one numeracy strategy', anchorId: 'plasp-section-numeracy' });
    if (selectedOtherGoal && !hasEnoughOtherStrategies) items.push({ label: 'Select at least one strategy for your optional goal', anchorId: 'plasp-section-other' });
    return items;
  }, [formValidation, hasEnoughLiteracyStrategies, hasEnoughNumeracyStrategies, hasEnoughOtherStrategies, selectedOtherGoal]);

  // Determine if we're in the document ready state
  const isDocumentReady = isDownloadVisible;

  return (
    <div className="bsp-form-container plasp-form-container">
      <div className="bsp-form-header">
        <h1>Create an Individual Education Plan</h1>
        <p className="form-description">
          Identify tailored strategies, goals, and supports to help a student succeed in their learning.
        </p>
      </div>

      {formState.submitError && (
        <div className="form-error-message">
          <p>Error: {formState.submitError}</p>
        </div>
      )}

      {formState.submitSuccess && !isDocumentReady && (
        <div className="form-success-message">
          <p>Your Personalised Learning and Support Plan has been successfully submitted!</p>
        </div>
      )}


      {!isDocumentReady && (
        <form onSubmit={handleSubmit}>
          {/* Green section heading for all boxes above the goals */}
          <div className="plasp-section-heading">Background information</div>
          <div id="plasp-section-student-info">
            <StudentInfoSection
              formData={formState.formData}
              onChange={handleTextChange}
              showTitle
            />
          </div>
          <div id="plasp-section-purpose">
            <PurposeSection
              formData={formState.formData}
              onTextChange={handleTextChange}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>
          <div id="plasp-section-learning">
            <LearningSection
              formData={formState.formData}
              onTextChange={handleTextChange}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>
          <div id="plasp-section-additional-info">
            <AdditionalInfoSection
              formData={formState.formData}
              onTextChange={handleTextChange}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>
          <div id="plasp-section-level-of-adjustment">
            <LevelOfAdjustmentSection
              formData={formState.formData}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>

          {/* Literacy Goal & Strategies Section */}
          <div id="plasp-section-literacy" className="plasp-section-heading">Literacy Goal</div>
          <LiteracyGoalSection
            formData={formState.formData}
            onTextChange={handleTextChange}
            onCheckboxChange={handleCheckboxChange}
            goals={literacyGoals}
            isLoading={loadingLiteracyGoals}
            error={literacyGoalsError}
            onGenerateGoals={generateLiteracyGoals}
            onGoalSelect={selectLiteracyGoal}
            selectedGoal={selectedLiteracyGoal}
            isFormValid={isFormValid}
            formValidation={formValidation}
          />
          <LiteracyStrategiesSection
            strategies={literacyStrategies}
            isLoading={loadingLiteracyStrategies}
            error={literacyStrategiesError}
            onGenerateStrategies={generateLiteracyStrategies}
            onStrategyToggle={toggleLiteracyStrategySelection}
            selectedStrategies={selectedLiteracyStrategies}
            smartGoal={selectedLiteracyGoal}
          />
          
          {/* Numeracy Goal & Strategies Section */}
          <div id="plasp-section-numeracy" className="plasp-section-heading">Numeracy Goal</div>
          <NumeracyGoalSection
            formData={formState.formData}
            onTextChange={handleTextChange}
            goals={numeracyGoals}
            isLoading={loadingNumeracyGoals}
            error={numeracyGoalsError}
            onGenerateGoals={generateNumeracyGoals}
            onGoalSelect={selectNumeracyGoal}
            selectedGoal={selectedNumeracyGoal}
            isFormValid={isFormValid}
            formValidation={formValidation}
          />
          <NumeracyStrategiesSection
            strategies={numeracyStrategies}
            isLoading={loadingNumeracyStrategies}
            error={numeracyStrategiesError}
            onGenerateStrategies={generateNumeracyStrategies}
            onStrategyToggle={toggleNumeracyStrategySelection}
            selectedStrategies={selectedNumeracyStrategies}
            smartGoal={selectedNumeracyGoal}
          />
          
          {/* Other Goal & Strategies Section */}
          <div id="plasp-section-other" className="plasp-section-heading">Other Goal</div>
          <OtherGoalSection
            formData={formState.formData}
            onTextChange={handleTextChange}
            goals={otherGoals}
            isLoading={loadingOtherGoals}
            error={otherGoalsError}
            onGenerateGoals={generateOtherGoals}
            onGoalSelect={selectOtherGoal}
            selectedGoal={selectedOtherGoal}
            isFormValid={isFormValid}
            formValidation={formValidation}
          />
          {formState.formData.otherGoalType && (
            <OtherStrategiesSection
              strategies={otherStrategies}
              isLoading={loadingOtherStrategies}
              error={otherStrategiesError}
              onGenerateStrategies={generateOtherStrategies}
              onStrategyToggle={toggleOtherStrategySelection}
              selectedStrategies={selectedOtherStrategies}
              smartGoal={selectedOtherGoal}
              goalType={formState.formData.otherGoalType}
            />
          )}
          
          <div className="form-group">
            <div className="form-info-box">
              <p className="info-primary">Gymea documents are all editable Word docs, so you can make changes to wording, fonts or appearance.</p>
              <p className="info-secondary">P.S. Opening your document on mobile devices might cause fonts or formatting to display differently to how they will appear when you open in the Word&nbsp;app.</p>
            </div>
          </div>

          <MissingFieldsSummary missingFields={missingFields} />

          <div className="form-actions">
            <button type="button" className="secondary-button" onClick={onBack}>
              Back
            </button>
            <button
              type="submit"
              className="primary-button"
              disabled={isSubmitting || !hasEnoughStrategies || !isFormValid || !canUse}
            >
              {isSubmitting ? 'Submitting...' : 'Draft a plan'}
            </button>
          </div>

          {/* Usage Indicator - shown below form actions */}
          {usage && (
            <div className="usage-indicators-container">
              <UsageIndicator
                productName={usage.name}
                used={usage.used}
                limit={usage.limit}
                remaining={usage.remaining}
                canUse={usage.can_use}
                period={new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long' })}
                loading={usageLoading}
              />
            </div>
          )}
        </form>
      )}
      
      {/* Document Download section - shown when document is ready */}
      {isDocumentReady && documentId && base64Content && (
        <div className="document-download-section">
          <DocumentDownload
            documentId={documentId}
            base64Content={base64Content}
            documentType="plasp"
            onClose={hideDownload}
          />
          
          <div className="form-actions document-actions">
            <button 
              type="button" 
              className="secondary-button" 
              onClick={hideDownload}
            >
              Edit Plan
            </button>
            <button
              type="button"
              className="primary-button"
              onClick={onBack}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
